import { getCurrentSession, isSupabaseConfigured } from "@/lib/supabase";
import { firefliesListParamsToSearchParams } from "@/lib/fireflies/buildFirefliesListParams";
import type {
  FirefliesListParams,
  FirefliesTranscriptDetail,
  FirefliesTranscriptResponse,
  FirefliesTranscriptsResponse,
} from "@/types/fireflies";

const SUPABASE_PROJECT_URL = "https://tsnbktwryvnbmgsvzxdt.supabase.co";

function getBaseUrl(): string {
  if (import.meta.env.DEV) {
    return "/api/fireflies";
  }
  return `${SUPABASE_PROJECT_URL}/functions/v1/fireflies-proxy`;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const session = await getCurrentSession();
  if (!session?.access_token) {
    throw new Error("Please log in to access Fireflies meetings");
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    "Content-Type": "application/json",
  };
}

async function parseError(response: Response): Promise<never> {
  let message = response.statusText || "Request failed";
  try {
    const body = await response.json();
    if (body?.error && typeof body.error === "string") {
      message = body.error;
    }
  } catch {
    // ignore
  }
  throw new Error(message);
}

class FirefliesService {
  async listTranscripts(params: FirefliesListParams = {}): Promise<FirefliesTranscriptsResponse> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured");
    }

    const limit = params.limit ?? 20;
    const skip = params.skip ?? 0;
    const query = firefliesListParamsToSearchParams({ ...params, limit, skip }).toString();
    const url = `${getBaseUrl()}/transcripts${query ? `?${query}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: await getAuthHeaders(),
    });

    if (!response.ok) {
      await parseError(response);
    }

    return response.json();
  }

  async getTranscript(transcriptId: string): Promise<FirefliesTranscriptDetail> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured");
    }

    const id = transcriptId.trim();
    if (!id) {
      throw new Error("Transcript ID is required");
    }

    const url = `${getBaseUrl()}/transcript/${encodeURIComponent(id)}`;
    const response = await fetch(url, {
      method: "GET",
      headers: await getAuthHeaders(),
    });

    if (!response.ok) {
      await parseError(response);
    }

    const data: FirefliesTranscriptResponse = await response.json();
    return data.transcript;
  }
}

export const firefliesService = new FirefliesService();

export function firefliesTranscriptsQueryKey(
  userId: string | undefined,
  params: FirefliesListParams
): readonly [string, string | undefined, FirefliesListParams] {
  return ["fireflies-transcripts", userId, params] as const;
}

export function firefliesTranscriptQueryKey(
  transcriptId: string | undefined
): readonly [string, string | undefined] {
  return ["fireflies-transcript", transcriptId] as const;
}
